import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Header } from '../components/Header';
import { volunteerApplicationService, shiftsService } from '../services/api';
import { useAuth } from '../hooks/useAuth';
import { parsePhoneNumberFromString } from 'libphonenumber-js';

export function VolunteerSignup() {
  const { user, loading: authLoading, logout } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [portalUrl, setPortalUrl] = useState('https://people.roskilde-festival.dk/volunteer');

  const [formData, setFormData] = useState({
    full_name: '',
    date_of_birth: '',
    phone: '',
    dtu_member_status: 'no' as 'yes_student_employee' | 'yes_alumni' | 'no',
    previous_volunteer: '',
    terms_agreed: false,
    has_peoplevol_profile: null as boolean | null,
    confirmed_peoplevol_registration: false,
  });

  useEffect(() => {
    if (authLoading) return;

    if (!user) {
      navigate('/login');
      return;
    }

    // Check application status
    const checkStatus = async () => {
      try {
        // Load settings to get portal URL
        const settingsRes = await shiftsService.getSettings();
        if (settingsRes.data?.peoplevol_portal_url) {
          setPortalUrl(settingsRes.data.peoplevol_portal_url);
        }

        const res = await volunteerApplicationService.getStatus();
        if (res.data.status === 'approved') {
          // Only navigate if we're on the signup page
          navigate('/');
        } else if (res.data.status === 'rejected') {
          setError('Your application was rejected. Please contact us for details.');
          setLoading(false);
        } else if (res.data.registration_completed) {
          // Already completed registration, go to success screen
          setStep(3);
          setLoading(false);
        } else if (res.data.terms_agreed) {
          setStep(2);
          setLoading(false);
        } else {
          setStep(1);
          setLoading(false);
        }
      } catch (err) {
        // If no application found, start from step 1
        setStep(1);
        setLoading(false);
      }
    };

    checkStatus();
  }, [authLoading, user]);

  const handleStep1Submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');

    // Validate phone number
    if (!validatePhoneNumber(formData.phone)) {
      setError('Please enter a valid phone number. Accepted formats: +45 12 34 56 78, 12 34 56 78, or similar.');
      setSubmitting(false);
      return;
    }

    const normalizedPhone = normalizePhoneForSubmit(formData.phone);

    try {
      // For Step 1, only send the basic form data, not the People-Vol profile fields
      const { has_peoplevol_profile, confirmed_peoplevol_registration, ...step1Data } = formData;
      await volunteerApplicationService.submitApplication({
        ...step1Data,
        phone: normalizedPhone,
        dtu_member_status: formData.dtu_member_status,
      } as any);
      // After successful submission, move to step 2 (don't navigate away)
      setStep(2);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to submit application');
    } finally {
      setSubmitting(false);
    }
  };

  const handleStep2Submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');

    try {
      // Only send the People-Vol fields - step 1 data is already saved in the DB
      await volunteerApplicationService.confirmRegistration({
        has_peoplevol_profile: formData.has_peoplevol_profile ?? undefined,
        confirmed_peoplevol_registration: true,
      });
      // After successful confirmation, show step 3 (completion message)
      setStep(3);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to confirm registration');
    } finally {
      setSubmitting(false);
    }
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-gray-600">Loading...</p>
      </div>
    );
  }

  if (!user) return null;

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const validatePhoneNumber = (phone: string): boolean => {
    const parsed = parsePhoneNumberFromString(phone, 'DK');
    if (parsed) {
      return parsed.isValid();
    }
    // Fallback: accept basic international/danish formats
    const phoneRegex = /^(\+)?[\d\s\-()]{7,}$/;
    const digitsOnly = phone.replace(/\D/g, '');
    return phoneRegex.test(phone) && digitsOnly.length >= 8 && digitsOnly.length <= 15;
  };

  const formatPhoneForDisplay = (phone: string): string => {
    const parsed = parsePhoneNumberFromString(phone, 'DK');
    if (parsed) {
      return parsed.formatInternational();
    }
    return phone;
  };

  const normalizePhoneForSubmit = (phone: string): string => {
    const parsed = parsePhoneNumberFromString(phone, 'DK');
    if (parsed) {
      return parsed.number;
    }
    return phone.trim();
  };

  return (
    <>
      <Header user={user} onLogout={handleLogout} />
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {step === 1 ? (
          // Step 1 - Application Form
          <div>
            {/* Welcome section */}
            <div className="mb-12 bg-gradient-to-r from-blue-50 to-blue-100 rounded-lg p-8 border border-blue-200">
              <h1 className="text-4xl font-bold text-gray-900 mb-4">
                🎉 Welcome to the Team!
              </h1>
              <p className="text-lg text-gray-700 mb-6">
                Thank you for your commitment to support Roskilde Festival as a volunteer! We are excited to have you in our team and can't wait for what lies ahead!
              </p>

              <div className="space-y-6">
                <div>
                  <h2 className="text-2xl font-semibold text-gray-900 mb-3">🚪 Our Task at the Festival</h2>
                  <p className="text-gray-700 mb-4">
                    As a volunteer, your role will be crucial: you'll be helping us check tickets/access permits at the festival gates.
                  </p>
                  <p className="text-gray-700 font-semibold mb-2">Here's what to expect:</p>
                  <ul className="list-disc list-inside space-y-2 text-gray-700">
                    <li><strong>Shift Details:</strong> You'll work 4 shifts in total, each lasting 8 hours (+ break).</li>
                    <li><strong>Dates:</strong> Saturday, 27 June 2026, to Sunday, 05 July 2026.</li>
                    <li><strong>Shift Types:</strong> Morning, evening, and night shifts (you'll work one of each, plus a fourth).</li>
                    <li><strong>Friends:</strong> We'll try our best to let you work with your friends, but we can't always guarantee it.</li>
                    <li><strong>Shift Assignment:</strong> You'll know your specific shifts a few weeks before the festival.</li>
                  </ul>
                </div>

                <div>
                  <h2 className="text-2xl font-semibold text-gray-900 mb-3">🎁 Volunteer Perks</h2>
                  <p className="text-gray-700 mb-2">What's in it for you? A lot!</p>
                  <ul className="list-disc list-inside space-y-2 text-gray-700">
                    <li>Free Access to the entire festival</li>
                    <li>Entry to the special Volunteer Village</li>
                    <li>Optional Volunteer Camping: A quiet area with clean showers and toilets</li>
                    <li>Free food vouchers and drinks for each shift</li>
                  </ul>
                </div>

                <div>
                  <h2 className="text-2xl font-semibold text-gray-900 mb-3">📧 Got Questions?</h2>
                  <p className="text-gray-700">
                    We're here to help! If you need any information or have questions, don't hesitate to reach out to us at:{' '}
                    <strong>roskilde@connectingworlds.dk</strong>
                  </p>
                </div>
              </div>
            </div>

            {/* Form */}
            <div className="bg-white shadow rounded-lg p-8">
              <h2 className="text-2xl font-bold mb-6">Volunteer Signup Form</h2>

              {error && <div className="bg-red-100 text-red-800 p-4 rounded mb-4">{error}</div>}

              <form onSubmit={handleStep1Submit} className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-900 mb-2">
                    Full Name *
                  </label>
                  <input
                    type="text"
                    required
                    maxLength={255}
                    value={formData.full_name}
                    onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                    className="w-full border border-gray-300 rounded px-4 py-2"
                    placeholder="Enter your full name"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    {formData.full_name.length}/255 Maximum of 255 characters
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-900 mb-2">
                    Date of Birth *
                  </label>
                  <input
                    type="date"
                    required
                    value={formData.date_of_birth}
                    onChange={(e) => setFormData({ ...formData, date_of_birth: e.target.value })}
                    className="w-full border border-gray-300 rounded px-4 py-2"
                  />
                  <p className="text-xs text-gray-500 mt-1">Minimum age: 18 years</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-900 mb-2">
                    Email *
                  </label>
                  <input
                    type="email"
                    disabled
                    value={user.email}
                    className="w-full border border-gray-300 rounded px-4 py-2 bg-gray-100 text-gray-600"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-900 mb-2">
                    Phone (WhatsApp) *
                  </label>
                  <input
                    type="tel"
                    required
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    onBlur={() => {
                      if (formData.phone.trim()) {
                        setFormData((prev) => ({ ...prev, phone: formatPhoneForDisplay(prev.phone) }));
                      }
                    }}
                    className={`w-full border rounded px-4 py-2 ${
                      formData.phone && !validatePhoneNumber(formData.phone)
                        ? 'border-red-500 bg-red-50'
                        : 'border-gray-300'
                    }`}
                    placeholder="+45 12 34 56 78"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Format: +45 12 34 56 78 or 12 34 56 78
                  </p>
                  {formData.phone && !validatePhoneNumber(formData.phone) && (
                    <p className="text-xs text-red-600 mt-1">
                      ✗ Invalid format. Please use 8-15 digits.
                    </p>
                  )}
                  {formData.phone && validatePhoneNumber(formData.phone) && (
                    <p className="text-xs text-green-600 mt-1">
                      ✓ Valid phone number
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-900 mb-2">
                    Are you from DTU? *
                  </label>
                  <div className="space-y-2">
                    <label className="flex items-center gap-3 cursor-pointer">
                      <input
                        type="radio"
                        name="dtu_member_status"
                        checked={formData.dtu_member_status === 'yes_student_employee'}
                        onChange={() => setFormData({ ...formData, dtu_member_status: 'yes_student_employee' })}
                        className="w-4 h-4"
                      />
                      <span className="text-sm text-gray-900">Yes (Student, Employee, ...)</span>
                    </label>
                    <label className="flex items-center gap-3 cursor-pointer">
                      <input
                        type="radio"
                        name="dtu_member_status"
                        checked={formData.dtu_member_status === 'yes_alumni'}
                        onChange={() => setFormData({ ...formData, dtu_member_status: 'yes_alumni' })}
                        className="w-4 h-4"
                      />
                      <span className="text-sm text-gray-900">Yes (Alumni)</span>
                    </label>
                    <label className="flex items-center gap-3 cursor-pointer">
                      <input
                        type="radio"
                        name="dtu_member_status"
                        checked={formData.dtu_member_status === 'no'}
                        onChange={() => setFormData({ ...formData, dtu_member_status: 'no' })}
                        className="w-4 h-4"
                      />
                      <span className="text-sm text-gray-900">No</span>
                    </label>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-900 mb-2">
                    Previous Volunteer
                  </label>
                  <p className="text-sm text-gray-600 mb-2">Did you volunteer with us before? If yes, select the year.</p>
                  <select
                    value={formData.previous_volunteer}
                    onChange={(e) => setFormData({ ...formData, previous_volunteer: e.target.value })}
                    className="w-full border border-gray-300 rounded px-4 py-2"
                  >
                    <option value="">No</option>
                    <option value="2023">2023</option>
                    <option value="2024">2024</option>
                    <option value="2025">2025</option>
                  </select>
                </div>

                <div>
                  <label className="flex items-start gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      required
                      checked={formData.terms_agreed}
                      onChange={(e) => setFormData({ ...formData, terms_agreed: e.target.checked })}
                      className="w-4 h-4 mt-1"
                    />
                    <span className="text-sm text-gray-700">
                      <strong>By signing up, you agree to the terms and conditions for volunteers at Roskilde Festival:</strong> You pledge to work your assigned shifts. You must be at the specified meeting point at least 15 minutes before the start of your shift. If you are absent from one or more of your shifts without a valid reason and without agreement with your manager, Roskilde Festival confiscates your wristband and charges a fee of DKK 4500. You will also lose the opportunity to volunteer at the festival for the next three years. You can only withdraw from volunteering without penalty if you find a replacement to take over your volunteer spot.
                      <br /><br />
                      Being drunk or under the influence of drugs is strictly forbidden during your shifts and will be counted as an absence, resulting in the aforementioned penalty. While working, the instructions of your supervisor must be followed. You are personally responsible for any damage or loss of equipment (vest, radio, …) provided to you for your shift and you must return it to the shift manager at the end of your shift.
                      <br /><br />
                      I agree that my personal data will be stored by Connecting Worlds DK and Roskilde Festival for the purpose of participation as a volunteer (see also: privacy policy).
                      <br /><br />
                      Please also refer to the FAQs: <a href="https://faq.roskilde-festival.dk/hc/en-gb/sections/14361365551773-Become-a-volunteer" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">https://faq.roskilde-festival.dk/hc/en-gb/sections/14361365551773-Become-a-volunteer</a>
                    </span>
                  </label>
                </div>

                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full bg-blue-600 text-white py-3 rounded font-semibold hover:bg-blue-700 disabled:bg-gray-400"
                >
                  {submitting ? 'Submitting...' : 'YES, I AGREE TO THE TERMS'}
                </button>
              </form>
            </div>
          </div>
        ) : step === 2 ? (
          // Step 2
          <div className="bg-white shadow rounded-lg p-8 max-w-2xl mx-auto">
            {/* Step progress */}
            <div className="flex items-center gap-2 mb-6 text-sm font-medium">
              <span className="flex items-center gap-1 text-green-600">
                <span className="w-6 h-6 rounded-full bg-green-600 text-white flex items-center justify-center text-xs font-bold">✓</span>
                Step 1: Application
              </span>
              <span className="flex-1 h-0.5 bg-gray-300 mx-1" />
              <span className="flex items-center gap-1 text-blue-700">
                <span className="w-6 h-6 rounded-full bg-blue-600 text-white flex items-center justify-center text-xs font-bold">2</span>
                Step 2: Roskilde Portal
              </span>
              <span className="flex-1 h-0.5 bg-gray-200 mx-1" />
              <span className="flex items-center gap-1 text-gray-400">
                <span className="w-6 h-6 rounded-full bg-gray-200 text-gray-500 flex items-center justify-center text-xs font-bold">3</span>
                Done
              </span>
            </div>

            {/* Incomplete banner */}
            <div className="bg-amber-50 border-l-4 border-amber-500 rounded-lg p-4 mb-6 flex items-start gap-3">
              <span className="text-amber-500 text-xl mt-0.5">⚠️</span>
              <div>
                <p className="font-semibold text-amber-800">Your application is not complete yet!</p>
                <p className="text-sm text-amber-700 mt-1">
                  You've finished step 1, but you must also complete this step before your application can be reviewed.
                </p>
              </div>
            </div>

            <h2 className="text-2xl font-bold mb-6">Step 2: Register with Roskilde Festival</h2>

            {error && <div className="bg-red-100 text-red-800 p-4 rounded mb-4">{error}</div>}

            {formData.has_peoplevol_profile === null ? (
              // Ask if they have existing profile
              <div className="space-y-6">
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
                  <p className="text-gray-700 mb-4">
                    Thanks for signing up with us! Now there's just one last step: you need to register on the official Roskilde Festival portal. This is how we assign your ticket and make everything official.
                  </p>
                  <p className="text-gray-700 font-medium">
                    Do you already have a Roskilde People-Vol account from this or a previous year?
                  </p>
                </div>

                <div className="flex gap-4">
                  <button
                    onClick={() => setFormData({ ...formData, has_peoplevol_profile: true })}
                    className="flex-1 bg-green-600 text-white py-3 rounded font-semibold hover:bg-green-700"
                  >
                    Yes, I have an account
                  </button>
                  <button
                    onClick={() => setFormData({ ...formData, has_peoplevol_profile: false })}
                    className="flex-1 bg-blue-600 text-white py-3 rounded font-semibold hover:bg-blue-700"
                  >
                    No, I need to register
                  </button>
                </div>
              </div>
            ) : formData.has_peoplevol_profile ? (
              // Has existing profile
              <div className="space-y-6">
                <div className="bg-green-50 border border-green-200 rounded-lg p-6">
                  <p className="text-green-800 font-medium mb-2">✓ Great! You already have a People-Vol account.</p>
                  <p className="text-gray-700">
                    We've noted that you have an existing account. Our team will verify this during the application review process.
                  </p>
                </div>

                <form onSubmit={handleStep2Submit}>
                  <button
                    type="submit"
                    disabled={submitting}
                    className="w-full bg-blue-600 text-white py-3 rounded font-semibold hover:bg-blue-700 disabled:bg-gray-400"
                  >
                    {submitting ? 'Confirming...' : 'Continue'}
                  </button>
                </form>

                <button
                  onClick={() => setFormData({ ...formData, has_peoplevol_profile: null })}
                  className="text-sm text-gray-600 hover:text-gray-800 underline w-full"
                >
                  ← Go back
                </button>
              </div>
            ) : (
              // Needs to register
              <div className="space-y-6">
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
                  <p className="text-gray-700 mb-4">
                    Please register on the official Roskilde Festival portal using the link below:
                  </p>
                  <a 
                    href={portalUrl}
                    target="_blank" 
                    rel="noopener noreferrer" 
                    className="inline-block bg-orange-500 text-white px-6 py-3 rounded font-semibold hover:bg-orange-600"
                  >
                    👉 Register on Roskilde Festival Portal
                  </a>
                </div>

                <form onSubmit={handleStep2Submit} className="space-y-6">
                  <div>
                    <label className="flex items-start gap-3 cursor-pointer">
                      <input
                        type="checkbox"
                        required
                        checked={formData.confirmed_peoplevol_registration}
                        onChange={(e) => setFormData({ ...formData, confirmed_peoplevol_registration: e.target.checked })}
                        className="w-4 h-4 mt-1"
                      />
                      <span className="text-sm text-gray-700">
                        ✅ I've completed my registration on the Roskilde Festival portal
                      </span>
                    </label>
                  </div>

                  <button
                    type="submit"
                    disabled={submitting}
                    className="w-full bg-blue-600 text-white py-3 rounded font-semibold hover:bg-blue-700 disabled:bg-gray-400"
                  >
                    {submitting ? 'Confirming...' : 'Confirm Completion'}
                  </button>
                </form>

                <button
                  onClick={() => setFormData({ ...formData, has_peoplevol_profile: null })}
                  className="text-sm text-gray-600 hover:text-gray-800 underline w-full"
                >
                  ← Go back
                </button>
              </div>
            )}

            <p className="text-xs text-gray-500 mt-6 text-center">
              Complete this step to submit your full application for review.
            </p>
          </div>
        ) : (
          // Step 3 - Application Submitted
          <div className="bg-white shadow rounded-lg p-8 max-w-2xl mx-auto text-center">
            <div className="mb-6">
              <div className="text-6xl mb-4">✅</div>
              <h2 className="text-3xl font-bold text-green-600 mb-4">Application Submitted!</h2>
            </div>

            <div className="space-y-4 text-gray-700 mb-8">
              <p className="text-lg">
                Thank you for your volunteer application! We've received your submission.
              </p>
              <p>
                Our team will review your application and get back to you within the next few days.
              </p>
              <p>
                In the meantime, you can check your email for any updates or reach out to us at{' '}
                <strong>roskilde@connectingworlds.dk</strong> if you have any questions.
              </p>
            </div>

            <button
              onClick={() => navigate('/login')}
              className="w-full bg-blue-600 text-white py-3 rounded font-semibold hover:bg-blue-700"
            >
              Return to Login
            </button>
          </div>
        )}
      </main>
    </>
  );
}
